// src/layouts/NetworkLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NetworkProvider, useNetwork } from '../context/NetworkContext';
import {
  X, Search, Users, Compass, MessageSquare, Bookmark, Globe, Home, Menu,
  Bell, Briefcase, ArrowLeft, User, Building, LogIn, LogOut, Settings
} from 'lucide-react';
import logo from '../assets/images/logo.png';
import MobileNetworkNav from '../components/network/layouts/MobileNetworkNav';
import NetworkRightSidebar from '../components/network/layouts/NetworkRightSidebar';
import TrendingTopics from '../components/network/discover/TrendingTopics';
import BusinessSuggestions from '../components/network/discover/BusinessSuggestions';
import { db } from '../firebase/config';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';

// NetworkHeader component with user profile image support
const NetworkHeader = ({ toggleSidebar, currentUser, userProfile, hasValidAccess, userBusinessId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadMessages, pendingConnectionCount } = useNetwork();
  const [searchValue, setSearchValue] = useState('');

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/network/discover?search=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue('');
    }
  };

  // Navigation items
  const navigation = [
    { name: 'Feed', path: '/network', icon: Globe },
    { name: 'Discover', path: '/network/discover', icon: Search },
    { name: 'My Connections', path: '/network/connections', icon: Users, badge: pendingConnectionCount },
    { name: 'Saved Posts', path: '/network/saved', icon: Bookmark },
    { name: 'Messages', path: '/network/messages', icon: MessageSquare, badge: unreadMessages },
  ];

  const isPublicPostView = !currentUser && location.pathname.startsWith('/network/post/');

  if (isPublicPostView) {
    return (
      <header className="bg-white shadow-sm">
        <div className="mx-auto flex h-16 max-w-7xl items-center px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center">
            <img className="h-8 w-auto" src={logo} alt="BizCentral" />
          </Link>
        </div>
      </header>
    );
  }

  return (
    <header className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left section: Logo and title */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center mr-4">
              <img className="h-8 w-auto" src={logo} alt="BizCentral" />
            </Link>
          </div>

          {/* Middle section: Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm ${
                  isActive(item.path)
                    ? 'text-[#190192]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <item.icon size={14} className="mr-1" />
                {item.name}
                {item.badge > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* Right section: Search and user actions */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="hidden md:flex relative w-64">
              <input
                type="text"
                placeholder="Search businesses..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-[#190192] focus:border-[#190192]"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={14} className="text-gray-400" />
              </div>
            </form>

            {/* My Business Profile link */}
            {currentUser && userBusinessId && (
              <Link
                to={`/network/profile/${userBusinessId}`}
                className="hidden md:flex items-center text-gray-600 hover:text-[#190192]"
                title="My Business Profile"
              >
                <Building size={16} />
              </Link>
            )}

            {/* Return to main site */}
            <Link
              to="/"
              className="flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={14} className="mr-1" />
            </Link>

            {/* Dashboard link */}
            {currentUser && (
              <Link
                to={hasValidAccess ? "/dashboard" : "/subscription/manage"}
                className="text-gray-600 hover:text-gray-800"
              >
                {hasValidAccess ? "Dashboard" : "Subscribe"}
              </Link>
            )}

            {/* User avatar */}
            {currentUser && (
              <div className="relative">
                <div className="h-8 w-8 rounded-full bg-[#190192] flex items-center justify-center text-white overflow-hidden">
                  {userProfile?.profileImageUrl ? (
                    <img
                      src={userProfile.profileImageUrl}
                      alt={`${userProfile.firstName || ''} ${userProfile.lastName || ''}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span>{currentUser.email?.charAt(0).toUpperCase() || 'U'}</span>
                  )}
                </div>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={toggleSidebar}
              className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

const NetworkLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, userProfile, subscription, isTrialing, isDemoMode, logout } = useAuth();
  const [mobileView, setMobileView] = useState('feed');
  const navigate = useNavigate();
  const location = useLocation();
  const [userBusinessId, setUserBusinessId] = useState(null);

  const hasValidAccess = subscription?.status === 'active' || isTrialing || isDemoMode;
  const isMessagesPage = location.pathname.startsWith('/network/messages');
  const isPublicPostView = !currentUser && location.pathname.startsWith('/network/post/');

  // Standalone pages that should render full-width without the 3-column layout
  const isStandalonePage = location.pathname.startsWith('/network/post/') ||
    location.pathname.startsWith('/network/profile/') ||
    location.pathname.startsWith('/network/tag/') ||
    location.pathname === '/network/discover' ||
    location.pathname === '/network/connections' ||
    location.pathname === '/network/saved';

  // Fetch user's business ID
  useEffect(() => {
    const fetchUserBusiness = async () => {
      if (!currentUser) return;
      try {
        const businessQuery = query(
          collection(db, 'businesses'),
          where('createdBy', '==', currentUser.uid),
          limit(1)
        );
        const snap = await getDocs(businessQuery);
        if (!snap.empty) {
          setUserBusinessId(snap.docs[0].id);
        }
      } catch (err) {
        console.error('Error fetching user business:', err);
      }
    };
    fetchUserBusiness();
  }, [currentUser]);

  const navigation = [
    { name: 'Network Home', path: '/network', icon: Home },
    { name: 'Discover', path: '/network/discover', icon: Compass },
    { name: 'My Connections', path: '/network/connections', icon: Users },
    { name: 'Saved Posts', path: '/network/saved', icon: Bookmark },
    { name: 'Messages', path: '/network/messages', icon: MessageSquare },
  ];

  // Sidebar contents for mobile view
  const SidebarContent = () => {
    const { pendingConnectionCount, unreadMessages } = useNetwork();

    return (
      <div className="h-full flex flex-col bg-white">
        <div className="px-4 py-4 flex items-center justify-between border-b">
          <Link to="/" className="flex items-center">
            <img className="h-8 w-auto" src={logo} alt="BizCentral" />
            <span className="ml-2 text-lg font-semibold text-[#190192]">Business Network</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <nav className="space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className="flex items-center justify-between px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                onClick={() => setSidebarOpen(false)}
              >
                <div className="flex items-center">
                  <item.icon size={18} className="mr-3 text-gray-500" />
                  {item.name}
                </div>
                {item.name === 'My Connections' && pendingConnectionCount > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingConnectionCount > 9 ? '9+' : pendingConnectionCount}
                  </span>
                )}
                {item.name === 'Messages' && unreadMessages > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* User profile section in mobile sidebar */}
          {currentUser ? (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="px-3 mb-4 flex items-center">
                <div className="w-10 h-10 rounded-full bg-[#190192] flex items-center justify-center text-white overflow-hidden mr-3">
                  {userProfile?.profileImageUrl ? (
                    <img
                      src={userProfile.profileImageUrl}
                      alt={`${userProfile.firstName || ''} ${userProfile.lastName || ''}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User size={20} />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {userProfile ? `${userProfile.firstName || ''} ${userProfile.lastName || ''}` : currentUser.email}
                  </p>
                  <p className="text-xs text-gray-500">{userProfile?.title || ''}</p>
                </div>
              </div>

              <div className="px-3 mb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Your Account
                </h3>
              </div>
              <nav className="space-y-1">
                <Link
                  to="/profile"
                  className="flex items-center px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                  onClick={() => setSidebarOpen(false)}
                >
                  <Settings size={18} className="mr-3 text-gray-500" />
                  Profile Settings
                </Link>
                {userBusinessId && (
                  <Link
                    to={`/network/profile/${userBusinessId}`}
                    className="flex items-center px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Building size={18} className="mr-3 text-gray-500" />
                    My Business Profile
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  className="flex items-center px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                  onClick={() => setSidebarOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/"
                  className="flex items-center px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                  onClick={() => setSidebarOpen(false)}
                >
                  Back to Main Site
                </Link>
                <button
                  type="button"
                  className="flex w-full items-center px-3 py-2 rounded-md text-red-600 hover:bg-red-50"
                  onClick={async () => {
                    setSidebarOpen(false);
                    await logout();
                    navigate('/login');
                  }}
                >
                  <LogOut size={18} className="mr-3" />
                  Sign Out
                </button>
              </nav>
            </div>
          ) : (
            <div className="mt-8 pt-6 border-t border-gray-200 px-3">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Account</h3>
              <div className="space-y-2">
                <Link
                  to="/login"
                  className="flex items-center rounded-md bg-[#190192] px-3 py-2 text-sm font-medium text-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <LogIn size={18} className="mr-3" />
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700"
                  onClick={() => setSidebarOpen(false)}
                >
                  <User size={18} className="mr-3 text-gray-500" />
                  Register
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <NetworkProvider>
      <div className={`min-h-screen bg-gray-100 ${isPublicPostView ? '' : 'pb-16 lg:pb-0'}`}>
        {/* Header */}
        <NetworkHeader
          toggleSidebar={() => setSidebarOpen(true)}
          currentUser={currentUser}
          userProfile={userProfile}
          hasValidAccess={hasValidAccess}
          userBusinessId={userBusinessId}
        />

        {/* Mobile sidebar */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            ></div>

            {/* Sidebar */}
            <div className="fixed inset-y-0 left-0 w-full max-w-xs z-50">
              <SidebarContent />
            </div>
          </div>
        )}

        {/* Main content with responsive layout */}
        <main className={`${isPublicPostView ? 'max-w-7xl mx-auto px-0 sm:px-2 lg:px-8 py-6' : 'max-w-7xl mx-auto px-0 sm:px-2 lg:px-8 py-6'}`}>
          {isMessagesPage || isStandalonePage ? (
            /* Full-width layout for standalone pages (post detail, profile, tags, etc.) */
            <Outlet />
          ) : (
            <>
              {/* Desktop: 3-column layout (30/50/20 ratio) */}
              <div className="hidden lg:flex lg:flex-nowrap">
                {/* Left sidebar - 25% */}
                <div className="lg:w-[25%] lg:pr-2">
                  <Outlet context={{ placement: 'left-sidebar' }} />
                </div>

                {/* Main content - 50% */}
                <div className="lg:w-[50%] lg:px-2 sm:px-0">
                  <Outlet context={{ placement: 'main' }} />
                </div>

                {/* Right sidebar messaging - 25% */}
                <div className="lg:w-[25%] lg:max-h-[450px] lg:pl-2">
                  <NetworkRightSidebar />
                  <Outlet context={{ placement: 'right-sidebar' }} />
                </div>
              </div>

              {/* Mobile: Single column with toggle */}
              <div className="lg:hidden">
                {mobileView === 'feed' && (
                  <div className="mb-6 px-0">
                    <Outlet context={{ placement: 'main' }} />
                  </div>
                )}

                {mobileView === 'suggestions' && (
                  <div className="mb-6 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                      <TrendingTopics />
                    </div>

                    {currentUser && (
                      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                        <BusinessSuggestions />
                      </div>
                    )}
                  </div>
                )}

                {mobileView === 'messages' && (
                  <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="h-[calc(100vh-16rem)] overflow-hidden">
                      <NetworkRightSidebar expanded={true} />
                    </div>
                    <div className="p-3 border-t border-gray-100 text-center">
                      <Link
                        to="/network/messages"
                        className="w-full px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-block"
                      >
                        View All Messages
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </main>

        {/* Mobile navigation bar */}
        {!isPublicPostView && !isMessagesPage && !isStandalonePage && (
          <MobileNetworkNav
            activeView={mobileView}
            onChangeView={setMobileView}
            currentUser={currentUser}
          />
        )}

        {/* Network footer */}
        {!isPublicPostView && (
          <footer className="bg-white border-t border-gray-200 mt-8 hidden lg:block">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center">
                <Globe size={20} className="text-[#190192] mr-2" />
                <p className="text-gray-700 font-medium">BizCentral Business Network</p>
              </div>
              <div className="mt-4 md:mt-0">
                <p className="text-gray-500 text-sm">Connect, Share, Grow Together</p>
              </div>
            </div>
          </div>
          </footer>
        )}
      </div>
    </NetworkProvider>
  );
};

export default NetworkLayout;
